class CollectionLoop extends HTMLElement {
  static STORAGE_KEY = 'grid-view';
  static DEFAULT_VIEW = 'grid-view-small';

  connectedCallback() {
    this.onGridViewChange = this.onGridViewChange.bind(this);

    this.querySelectorAll('#grid-view-small, #grid-view-large').forEach((input) => {
      input.addEventListener('change', this.onGridViewChange);
    });

    this.applyGridView(this.getStoredGridView());
    this.initLazyLoad();

    const productGridContainer = this.querySelector('#ProductGridContainer');
    if (productGridContainer) {
      this.gridObserver = new MutationObserver(() => {
        if (this.querySelector('.collection__grid')) {
          this.applyGridView(this.getStoredGridView());
          this.initLazyLoad();
        }
      });
      this.gridObserver.observe(productGridContainer, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('#grid-view-small, #grid-view-large').forEach((input) => {
      input.removeEventListener('change', this.onGridViewChange);
    });
    this.gridObserver?.disconnect();
    this.destroyLazyLoadObserver();
  }

  getCollectionWrap() {
    return this.querySelector('[data-collection]:not(.collection__wrap--empty)');
  }

  getAppendGrid() {
    return this.querySelector('[data-collection-continuation]') || this.querySelector('#product-grid');
  }

  getViewGrids() {
    return this.querySelectorAll('.collection__grid');
  }

  initLazyLoad() {
    const wrap = this.getCollectionWrap();
    const nextUrl = wrap?.dataset.nextUrl || null;
    const sentinel = wrap?.querySelector('[data-collection-loop-sentinel]');

    // Skip if pagination state hasn't actually changed — without this guard,
    // any unrelated mutation inside the grid (e.g. a product-card slider
    // mounting on hover) tears down and recreates the observer; if the
    // sentinel is already within rootMargin, the fresh observer fires
    // immediately, calling loadMore(), which appends more products (another
    // mutation), which retriggers this again — a runaway fetch loop that
    // balloons the DOM/memory until the tab crashes.
    if (sentinel === this.sentinel && nextUrl === this.nextUrl) return;

    this.destroyLazyLoadObserver();
    this.nextUrl = nextUrl;
    this.sentinel = sentinel;

    if (!wrap || !this.sentinel || !this.nextUrl) return;

    this.loadingMore = false;
    this.lazyLoadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { rootMargin: '400px 0px' }
    );
    this.lazyLoadObserver.observe(this.sentinel);
  }

  destroyLazyLoadObserver() {
    this.lazyLoadObserver?.disconnect();
    this.lazyLoadObserver = null;
  }

  buildSectionUrl(url) {
    const sectionId = this.dataset.sectionId;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${sectionId}`;
  }

  async loadMore() {
    if (this.loadingMore || !this.nextUrl) return;

    const wrap = this.getCollectionWrap();
    const appendGrid = this.getAppendGrid();
    if (!wrap || !appendGrid) return;

    this.loadingMore = true;
    wrap.classList.add('loading');

    try {
      const response = await fetch(this.buildSectionUrl(this.nextUrl));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedWrap = doc.querySelector('[data-collection]:not(.collection__wrap--empty)');
      const newItems = doc.querySelectorAll('#product-grid .grid__item');

      newItems.forEach((item) => appendGrid.appendChild(item));

      const nextUrl = fetchedWrap?.dataset.nextUrl || null;
      const nextSentinel = fetchedWrap?.querySelector('[data-collection-loop-sentinel]');

      if (nextUrl && nextSentinel) {
        wrap.dataset.nextUrl = nextUrl;
        this.nextUrl = nextUrl;
        this.sentinel.replaceWith(nextSentinel);
        this.sentinel = nextSentinel;
        this.lazyLoadObserver.observe(this.sentinel);
      } else {
        delete wrap.dataset.nextUrl;
        this.nextUrl = null;
        this.sentinel?.remove();
        this.sentinel = null;
        this.destroyLazyLoadObserver();
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }
    } catch (error) {
      console.error(error);
    } finally {
      wrap.classList.remove('loading');
      this.loadingMore = false;
    }
  }

  getStoredGridView() {
    const stored = sessionStorage.getItem(CollectionLoop.STORAGE_KEY);
    if (stored === 'grid-view-small' || stored === 'grid-view-large') return stored;
    return CollectionLoop.DEFAULT_VIEW;
  }

  applyGridView(viewId) {
    const isLarge = viewId === 'grid-view-large';

    this.getViewGrids().forEach((grid) => {
      grid.classList.toggle('collection__grid--large', isLarge);
    });

    const radio = this.querySelector(`#${viewId}`);
    if (radio) radio.checked = true;
  }

  onGridViewChange(event) {
    const { id } = event.target;
    if (id !== 'grid-view-small' && id !== 'grid-view-large') return;

    sessionStorage.setItem(CollectionLoop.STORAGE_KEY, id);
    this.applyGridView(id);
  }
}

customElements.define('collection-loop', CollectionLoop);
